import React, { useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { api } from "@/lib/api";
import {
  Upload, X, FileSpreadsheet, ArrowRight, ArrowLeft, Check,
  AlertTriangle, Loader2, Sparkles, Undo2, ShieldCheck,
} from "lucide-react";

const TARGET_FIELDS = [
  { key: "name", label: "Name", hints: ["name", "full name", "contact", "first name"] },
  { key: "email", label: "Email", hints: ["email", "e-mail", "mail", "address"] },
  { key: "company", label: "Company", hints: ["company", "org", "organization", "business"] },
  { key: "title", label: "Title", hints: ["title", "role", "position", "job"] },
  { key: "phone", label: "Phone", hints: ["phone", "tel", "mobile"] },
  { key: "city", label: "City", hints: ["city", "town"] },
  { key: "state", label: "State", hints: ["state", "region", "province"] },
  { key: "industry", label: "Industry", hints: ["industry", "sector", "category"] },
  { key: "linkedin", label: "LinkedIn", hints: ["linkedin", "linked in", "linkedin url"] },
  { key: "score", label: "Score", hints: ["score", "rank", "priority", "rating"] },
  { key: "subject", label: "Draft subject", hints: ["subject", "email subject"] },
  { key: "body", label: "Draft body", hints: ["body", "message", "email body", "copy", "template"] },
  { key: "notes", label: "Notes", hints: ["notes", "note", "comment", "rationale"] },
];

function autoDetect(headers) {
  const map = {};
  headers.forEach((h) => {
    const hl = String(h || "").toLowerCase().trim();
    if (!hl) return;
    for (const t of TARGET_FIELDS) {
      if (map[t.key]) continue;
      if (t.hints.some((hint) => hl === hint || hl.includes(hint))) {
        map[t.key] = h;
        break;
      }
    }
  });
  return map;
}

function randomBatchId() {
  return `up-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function UploadLeadsModal({ open, onClose, lists, onDone }) {
  const [step, setStep] = useState(1); // 1=file, 2=mapping, 3=results
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [listChoice, setListChoice] = useState("__none__"); // list id | __none__ | __new__
  const [newListName, setNewListName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parseErr, setParseErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);

  const reset = useCallback(() => {
    setStep(1); setFile(null); setHeaders([]); setRows([]); setMapping({});
    setListChoice("__none__"); setNewListName(""); setParseErr(""); setResult(null);
    setUploading(false); setUndoing(false); setSavingCampaign(false);
  }, []);

  const handleClose = () => { reset(); onClose(); };

  const parseFile = async (f) => {
    setParsing(true); setParseErr("");
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const firstSheet = wb.SheetNames[0];
      const ws = wb.Sheets[firstSheet];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: false });
      if (json.length < 2) {
        setParseErr("File must have a header row and at least one data row.");
        setParsing(false); return;
      }
      const hdrs = json[0].map((h) => String(h || "").trim()).filter(Boolean);
      const dataRows = json.slice(1).map((r) => {
        const o = {};
        hdrs.forEach((h, i) => { o[h] = r[i]; });
        return o;
      }).filter((r) => Object.values(r).some((v) => String(v ?? "").trim() !== ""));
      if (dataRows.length === 0) {
        setParseErr("No non-empty data rows found.");
        setParsing(false); return;
      }
      if (dataRows.length > 500) {
        setParseErr(`Too many rows (${dataRows.length}). Max 500 per upload — please split the file.`);
        setParsing(false); return;
      }
      setHeaders(hdrs);
      setRows(dataRows);
      setMapping(autoDetect(hdrs));
      setStep(2);
    } catch (e) {
      setParseErr("Could not parse this file. Save as CSV or XLSX and try again.");
    } finally {
      setParsing(false);
    }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setParseErr("File is larger than 5 MB. Please split or trim it.");
      return;
    }
    setFile(f);
    await parseFile(f);
  };

  const previewRows = useMemo(() => rows.slice(0, 10), [rows]);

  const validationHint = useMemo(() => {
    if (!mapping.email && !(mapping.name && mapping.company)) {
      return "Map Email — or map both Name and Company — before importing.";
    }
    return null;
  }, [mapping]);

  const commit = async () => {
    setUploading(true);
    const batch_id = randomBatchId();
    const payload_rows = rows.map((r) => {
      const o = { };
      TARGET_FIELDS.forEach((t) => {
        const src = mapping[t.key];
        if (src && r[src] != null && r[src] !== "") {
          o[t.key] = t.key === "score" ? Number(r[src]) || undefined : String(r[src]).trim();
        }
      });
      return o;
    });
    const body = { batch_id, rows: payload_rows };
    if (listChoice === "__new__" && newListName.trim()) body.new_list_name = newListName.trim();
    else if (listChoice !== "__none__" && listChoice !== "__new__") body.list_id = listChoice;
    try {
      const { data } = await api.post("/leads/upload/commit", body);
      setResult(data);
      setStep(3);
      onDone?.();
    } catch (e) {
      const raw = e.response?.data?.detail;
      setParseErr(typeof raw === "string" ? raw : "Import failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const undo = async () => {
    if (!result?.batch_id) return;
    if (!window.confirm(`Undo this import? All ${result.inserted} leads created will be deleted.`)) return;
    setUndoing(true);
    try {
      await api.post(`/leads/upload/undo/${result.batch_id}`);
      onDone?.();
      handleClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Undo failed.");
    } finally {
      setUndoing(false);
    }
  };

  const saveAsCampaign = async () => {
    if (!result?.drafts_created) return;
    setSavingCampaign(true);
    try {
      await api.post("/campaigns", {
        name: `Uploaded outreach — ${new Date().toLocaleDateString()}`,
        channel: "email",
        subject: "Uploaded outreach",
        message: `This campaign was populated from ${result.drafts_created} pre-drafted rows in upload batch ${result.batch_id}. Individual subject/body pairs are stored per lead.`,
        steps: result.drafts_created,
      });
      setResult({ ...result, campaign_saved: true });
    } catch (e) {
      alert(e.response?.data?.detail || "Could not save campaign.");
    } finally {
      setSavingCampaign(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="upload-leads-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full max-w-3xl bg-zinc-950 border border-blue-500/30 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="label-eyebrow mb-1 flex items-center gap-2">
              <Upload className="w-3 h-3 text-blue-300" />
              Upload leads
            </div>
            <div className="font-display text-lg text-white">
              {step === 1 && "Choose a CSV or Excel file"}
              {step === 2 && "Map columns & set options"}
              {step === 3 && "Import complete"}
            </div>
          </div>
          <button onClick={handleClose} className="text-zinc-400 hover:text-white" data-testid="upload-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* STEP 1: FILE */}
        {step === 1 && (
          <div className="p-6">
            <label className="block glass-card p-10 border-dashed border-2 border-white/10 hover:border-blue-500/40 transition-colors cursor-pointer text-center" data-testid="upload-file-drop">
              <FileSpreadsheet className="w-10 h-10 text-blue-300 mx-auto mb-3" />
              <div className="text-white font-medium mb-1">Click to pick a file</div>
              <div className="text-xs text-zinc-500 mb-4">CSV or XLSX · up to 500 rows · up to 5 MB</div>
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={onFileChange}
                className="hidden"
                data-testid="upload-file-input"
              />
              <span className="btn-primary text-sm inline-flex pointer-events-none">
                {parsing ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</> : <><Upload className="w-4 h-4" /> Choose file</>}
              </span>
            </label>
            {parseErr && (
              <div className="mt-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200" data-testid="upload-parse-error">{parseErr}</div>
            )}
            <div className="mt-6 text-xs text-zinc-500 leading-relaxed">
              Your file stays in your browser during parsing — only mapped fields for the rows you commit are sent to the server.
            </div>
          </div>
        )}

        {/* STEP 2: MAPPING */}
        {step === 2 && (
          <div className="p-6 max-h-[70vh] overflow-y-auto space-y-5">
            <div className="text-sm text-zinc-400">
              <span className="text-white font-medium">{rows.length}</span> data row{rows.length === 1 ? "" : "s"} detected from <span className="text-white">{file?.name}</span>. Map each target field to one of your columns.
            </div>

            {/* Compliance reminder */}
            <div className="glass-card p-3 border-amber-400/30 bg-amber-400/[0.05] flex items-start gap-2" data-testid="upload-compliance">
              <ShieldCheck className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-200 leading-relaxed">
                You are the sender. Please ensure you have permission or a legitimate basis to email these contacts under CAN-SPAM / GDPR / your local rules.
              </div>
            </div>

            {/* Mapping grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {TARGET_FIELDS.map((t) => (
                <div key={t.key} className="glass-card p-3">
                  <label className="label-eyebrow mb-1.5 block">{t.label}</label>
                  <select
                    value={mapping[t.key] || ""}
                    onChange={(e) => setMapping((m) => ({ ...m, [t.key]: e.target.value || undefined }))}
                    className="input-glass w-full text-sm"
                    data-testid={`upload-map-${t.key}`}
                  >
                    <option value="">— none —</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {/* Preview */}
            <div>
              <div className="label-eyebrow mb-2">Preview (first {previewRows.length} of {rows.length})</div>
              <div className="glass-card overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/5 text-zinc-500">
                      {["Name", "Email", "Company", "Draft?"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => {
                      const nm = mapping.name ? r[mapping.name] : "";
                      const em = mapping.email ? r[mapping.email] : "";
                      const co = mapping.company ? r[mapping.company] : "";
                      const hasCopy = (mapping.subject && r[mapping.subject]) || (mapping.body && r[mapping.body]);
                      return (
                        <tr key={i} className="border-b border-white/5" data-testid={`upload-preview-row-${i}`}>
                          <td className="px-3 py-2 text-white">{String(nm || "—")}</td>
                          <td className="px-3 py-2 text-zinc-300">{String(em || "—")}</td>
                          <td className="px-3 py-2 text-zinc-300">{String(co || "—")}</td>
                          <td className="px-3 py-2">
                            {hasCopy ? (
                              <span className="text-emerald-300 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 inline-flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" />Draft</span>
                            ) : <span className="text-zinc-600">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* List assignment */}
            <div className="glass-card p-3">
              <div className="label-eyebrow mb-2">Add all imported leads to a list (optional)</div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={listChoice} onChange={(e) => setListChoice(e.target.value)} className="input-glass text-sm" data-testid="upload-list-select">
                  <option value="__none__">— No list —</option>
                  <option value="__new__">+ Create new list…</option>
                  {(lists || []).map((l) => (
                    <option key={l.id} value={l.id}>{l.name} ({l.count})</option>
                  ))}
                </select>
                {listChoice === "__new__" && (
                  <input
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="New list name…"
                    maxLength={60}
                    className="input-glass text-sm flex-1 min-w-[140px]"
                    data-testid="upload-new-list-name"
                  />
                )}
              </div>
            </div>

            {validationHint && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200" data-testid="upload-validation-hint">{validationHint}</div>
            )}
            {parseErr && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200">{parseErr}</div>
            )}
          </div>
        )}

        {/* STEP 3: RESULTS */}
        {step === 3 && result && (
          <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="glass-card p-3">
                <div className="label-eyebrow mb-1">Inserted</div>
                <div className="font-display text-2xl text-emerald-300" data-testid="upload-result-inserted">{result.inserted}</div>
              </div>
              <div className="glass-card p-3">
                <div className="label-eyebrow mb-1">Drafts</div>
                <div className="font-display text-2xl text-blue-300" data-testid="upload-result-drafts">{result.drafts_created}</div>
              </div>
              <div className="glass-card p-3">
                <div className="label-eyebrow mb-1">Duplicates</div>
                <div className="font-display text-2xl text-amber-300" data-testid="upload-result-dups">{result.skipped_dups}</div>
              </div>
              <div className="glass-card p-3">
                <div className="label-eyebrow mb-1">Rejected</div>
                <div className="font-display text-2xl text-rose-300" data-testid="upload-result-rejected">{result.rejected?.length || 0}</div>
              </div>
            </div>

            {result.rejected && result.rejected.length > 0 && (
              <div className="glass-card p-3">
                <div className="label-eyebrow mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3 text-rose-300" /> Rejected rows</div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {result.rejected.map((r, i) => (
                    <div key={i} className="text-xs text-zinc-300 flex items-center gap-2" data-testid={`upload-rejected-${i}`}>
                      <span className="text-zinc-500 font-mono">row {r.row_index + 2}</span>
                      <span>{r.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.skipped_dups > 0 && (
              <div className="text-xs text-amber-200 leading-relaxed">
                {result.skipped_dups} row{result.skipped_dups === 1 ? " was" : "s were"} skipped because the email already exists in your workspace.
              </div>
            )}

            <div className="glass-card p-3 border-amber-400/30 bg-amber-400/[0.05] flex items-start gap-2">
              <Undo2 className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-200 leading-relaxed">
                You can undo this entire import within the next 24 hours. After that, use "Clean up" or per-lead delete.
              </div>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div className="p-6 border-t border-white/5 flex items-center justify-between gap-3 flex-wrap">
          {step === 1 && (
            <div className="text-xs text-zinc-500 flex-1">Auto-detects Name / Email / Company columns.</div>
          )}
          {step === 2 && (
            <>
              <button onClick={() => setStep(1)} className="btn-ghost text-sm" data-testid="upload-back">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={commit}
                disabled={uploading || !!validationHint}
                className="btn-primary text-sm"
                data-testid="upload-import"
              >
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <>Import {rows.length} row{rows.length === 1 ? "" : "s"} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </>
          )}
          {step === 3 && result && (
            <>
              <button onClick={undo} disabled={undoing} className="text-xs text-amber-300 hover:text-amber-200 disabled:opacity-50" data-testid="upload-undo">
                {undoing ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : <Undo2 className="w-3.5 h-3.5 inline mr-1" />} Undo import
              </button>
              <div className="flex items-center gap-2 ml-auto">
                {result.drafts_created > 0 && (
                  <button
                    onClick={saveAsCampaign}
                    disabled={savingCampaign || result.campaign_saved}
                    className="btn-ghost text-sm"
                    data-testid="upload-save-campaign"
                  >
                    {savingCampaign ? <Loader2 className="w-4 h-4 animate-spin" /> : result.campaign_saved ? <><Check className="w-4 h-4" /> Saved</> : <><Sparkles className="w-4 h-4" /> Save drafts as campaign</>}
                  </button>
                )}
                <button onClick={handleClose} className="btn-primary text-sm" data-testid="upload-done">
                  Done <Check className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
